import { useRouteMatch } from "react-router-dom";
import { useGetReceiveTransferDetails } from "./api/useGetReceiveTransferDetails";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { translateError } from "../../util/axiosUtils";
import ScanEditWithSaveButton from "../../components/Scan/ScanEditWithSaveButton";
import { MemorizedInvoiceLineItemTable } from "../../components/Invoice/InvoiceLineItemTable";
import { InvoiceLineItem } from "../../common/InvoiceLineItem";
import PageContext from "../../components/Common/PageContext";
import { ProductGroupResponse } from "../../common/api/Models/ProductGroupResponse";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import {
	editLocationBay,
	isInboundLocation,
	isLocation,
	mapLocationBaysFromReceiveLineItems,
} from "../../util/locationTypeUtil";
import { getScannedItemByProductId, isEditable } from "../../util/receiveUtil";
import { isFeatureEnabledForLocationAndReceiveType } from "../../util/featureUtil";
import { receiveType } from "../../util/receiveTypeMapping";
import CurrentLocationBayAlert from "../../components/CurrentLocationBayAlert";
import { LocationBay } from "../../components/Common/LocationBay/LocationBay";
import ModalAssignLocation from "../../components/Modal/ModalAssignLocation";
import CommercialSelect from "../../components/Select/CommercialSelect";
import { Alert, Backdrop, Grid2 as Grid } from "@mui/material";
import ReceiveTransferHeaderDetails from "../../components/Transfer/TransferReceiving/ReceiveTransferHeaderDetails";
import { ModalAlertType, ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import { modals } from "../../util/modalUtil";
import {
	blockEdiScanStatusList,
	canBeClearedStatusList,
	canBeFinalisedStatusList,
	receiveStatus,
} from "../../util/receiveStatus";
import { UserContext } from "../../providers/user";
import { saveEventHistoryToList } from "../../util/historyStorageHelper";
import { sortItemsByModifiedDesc, sortItemsByPriorityStockFlag } from "../../util/sortUtil";
import { usePostReceive } from "../../common/api/usePostReceive";
import { Receive, ReceiveComment } from "../../common/Receive";
import { speedDialAction } from "../../util/speedDialUtil";
import SpeedDialMenu from "../../components/SpeedDial/SpeedDialMenu";
import { snackBarAlerts, SnackbarAlertState } from "../../util/snackBarAlert";
import SnackbarAlert from "../../components/Snackbar/SnackbarAlert";
import { ReceiveHistory } from "../../components/History/ReceiveHistory";
import modalSize from "../../util/modalSizeEnum";
import {
	createOrUpdateTransferReceive,
	createReceiveLineItemFromInvoiceLineItem,
	getTransferReceiveVariances,
	isTransferCompleted,
	isTransferReceivedDirectInHost,
	getUnverifiedQuantityDifferences,
} from "../../util/transferUtil";
import ModalReceiveFinalise from "../../components/Modal/ModalReceiveFinalise";
import { ModalUnverifiedTransferFinalise } from "../../components/Modal/ModalUnverifiedTransferFinalise";
import { postSubmitReceiveAsync } from "../../services/apiHelper";
import { AllocatedCustomerOrder } from "../../common/CustomerOrder";
import { ModalTransferReceiveVariancesFinalise } from "../../components/Modal/ModalTransferReceiveVariancesFinalise";
import { Transfer, TransferComment, TransferReceiveVariance } from "../../common/Transfer";
import { transferStatus } from "../../util/transferStatus";
import { getTransferTypeDisplayLabel, isVirtualTransfer } from "../../util/transferType";
import useQuery from "../../useQuery";
import { ModalHistory } from "../../components/Modal/ModalHistory";
import alertTypeEnum from "../../util/alertTypeEnum";
import { transferSourceSystem } from "../../util/transferSourceSystem";
import { usePostLineItemReceived } from "../../common/api/usePostLineItemReceived";
import { CommentModal } from "../../components/Modal/CommentModal";
import { useComments } from "../Receiving/hooks/useComments";
import { statusToLabel } from "../../util/statusMapping";

const mapTransferCommentToReceiveComment = (comment: TransferComment): ReceiveComment => ({
	id: comment.id ?? "",
	receiveId: "",
	createdTimestamp: comment.dateCreated ?? "",
	createdByUserId: comment.userId ?? "",
	createdByUserName: comment.userName ?? "",
	text: comment.content,
	sourceSystem: comment.sourceSystem ?? "",
});

type AssignModalState = {
	/* Set when changing the location bay for a specific sku */
	sku: string;
	/* The location bay currently assigned to the line item(s) */
	currentLocation: string;
	/* The location bay being scanned by the user */
	scannedLocation: string;
	context: "manual" | "scanned";
	isModalOpen: boolean;
};

type HistoryModalState = {
	/* Set when viewing history for a sku from the line items table */
	sku?: string;
	isModalOpen: boolean;
};

const ReceiveTransfer = () => {
	const { id } = useRouteMatch<{ id: string }>().params;
	const timestamp = useQuery().get("t");
	const [{ data: transfer, loading: isLoading, error: apiError }, refreshTransfer] = useGetReceiveTransferDetails(
		id,
		"",
		true,
		true
	);
	const [{ loading: isPosting }, postReceiveAsync] = usePostReceive(true);
	const [, postLineItemReceived] = usePostLineItemReceived();
	const [pendingReceive, setPendingReceive] = useState<boolean>(false);

	// Ref mirrors the `receive` state but updates synchronously, preventing stale-closure
	// race conditions where multiple scans in the same render cycle each see receive as null
	// and create duplicate receives.
	const receiveRef = useRef<Receive | null>(null);

	useEffect(() => {
		void refreshTransfer();
	}, [refreshTransfer, timestamp]);

	const [pageContext] = useContext(PageContext);
	const {
		putAwayLocations,
		productGroups,
		featureConfiguration: {
			inboundLocationAssignment,
			commercialAccountSelection,
			productLabelAutoPrint,
			priorityStock,
			arrivals,
		},
		userLocation,
		commercialAccounts,
	} = pageContext;

	const { graphProfile } = useContext(UserContext);
	const [receiveFinaliseModalIsOpen, setReceiveFinaliseModalIsOpen] = useState(false);
	const [clearReceiveModalIsOpen, setClearReceiveModalIsOpen] = useState(false);
	const [isHardwareView, setIsHardwareView] = useState(true);
	const [speedDialOpen, setSpeedDialOpen] = useState(false);
	const [currentInboundLocation, setCurrentInboundLocation] = useState("");
	const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);
	const [receiveHeaderExpanded, setReceiveHeaderExpanded] = useState(true);
	const [receive, setReceive] = useState<Receive | null>(null);
	const [receiveComments, setReceiveComments] = useState<ReceiveComment[]>([]);
	const [receivedDate, setReceivedDate] = useState<Date | null>(null);
	const [isDeliveredDateDisabled, setIsDeliveredDateDisabled] = useState(false);
	const [selectedCommercialAccount, setSelectedCommercialAccount] = useState<string>("");
	const [isShowLoadingDialog, setIsShowLoadingDialog] = useState(false);
	const [notes, setNotes] = useState<string>("");

	const [variances, setVariances] = useState<{
		overReceivedItems: TransferReceiveVariance[];
		underReceivedItems: TransferReceiveVariance[];
	}>({ overReceivedItems: [], underReceivedItems: [] });

	const [receiveWithVarianceModalIsOpen, setReceiveWithVarianceModalIsOpen] = useState(false);
	const [unverifiedFinaliseModalIsOpen, setUnverifiedFinaliseModalIsOpen] = useState(false);

	const hasLabels = useMemo(() => {
		if (!transfer) return false;

		return transfer.products.some((pd) => pd.hasLabel);
	}, [transfer]);

	const isUnverified = useMemo(() => Boolean(transfer?.transfer?.isUnverified), [transfer]);

	const isVirtual = useMemo(() => isVirtualTransfer(transfer?.transfer?.type), [transfer]);

	// Virtual transfers can only be received while In Transit; once completed the finalise action is hidden.
	const canFinaliseVirtual = useMemo(
		() => isVirtual && transfer?.transfer?.status === transferStatus.inTransit,
		[isVirtual, transfer]
	);

	const enableProductLabelAutoPrint = useMemo(() => {
		if (!transfer?.transfer) return false;

		return isFeatureEnabledForLocationAndReceiveType(
			transfer.transfer.toLocation.locationId,
			productLabelAutoPrint,
			receiveType.transfer
		);
	}, [productLabelAutoPrint, transfer]);

	const enableAssignIbLocation = useMemo(() => {
		if (!transfer?.transfer) return false;

		return isFeatureEnabledForLocationAndReceiveType(
			transfer.transfer.toLocation.locationId,
			inboundLocationAssignment,
			receiveType.transfer
		);
	}, [inboundLocationAssignment, transfer]);

	const enabledPriorityStock = useMemo(() => {
		if (!transfer?.transfer) return false;

		return isFeatureEnabledForLocationAndReceiveType(
			transfer.transfer.toLocation.locationId,
			priorityStock,
			receiveType.transfer
		);
	}, [priorityStock, transfer]);

	const enabledArrivals = useMemo(() => {
		if (!transfer?.transfer) return false;

		return isFeatureEnabledForLocationAndReceiveType(
			transfer.transfer.toLocation.locationId,
			arrivals,
			receiveType.transfer
		);
	}, [arrivals, transfer]);

	const enableCommercialAccountSelection = useMemo(() => {
		if (!transfer?.transfer) return false;

		return (
			isFeatureEnabledForLocationAndReceiveType(
				transfer.transfer.toLocation.locationId,
				commercialAccountSelection,
				receiveType.transfer
			) &&
			commercialAccounts.length > 0 &&
			userLocation === transfer.transfer.toLocation.locationId
		);
	}, [commercialAccountSelection, commercialAccounts, userLocation, transfer]);

	const [currentProduct, setCurrentProduct] = useState<{ id: string; qty: number }>({ id: "", qty: 0 });
	const [historyModalState, setHistoryModalState] = useState<HistoryModalState>({
		sku: "",
		isModalOpen: false,
	});
	const [assignLocationModal, setAssignLocationModal] = useState<AssignModalState>({
		sku: "",
		currentLocation: "",
		isModalOpen: false,
		scannedLocation: "",
		context: "manual",
	});
	const [snackbarAlert, setSnackbarAlert] = useState<SnackbarAlertState>({
		isOpen: false,
		message: "",
		severity: "success",
	});

	const matchedReceive =
		transfer?.receives.filter((receive) => receive.status !== receiveStatus.cancelled).pop() ?? null;

	const mergedInitialComments = useMemo(
		() => [
			...(receive?.comments ?? []),
			...(transfer?.transfer?.comments ?? []).map(mapTransferCommentToReceiveComment),
		],
		[receive, transfer]
	);

	const { comments, commentModal, onCommentModalOpen, onCommentModalClose, onAddComment, onAddTransferComment } =
		useComments(mergedInitialComments);

	useEffect(() => {
		const rawDate = matchedReceive?.receivedDate ? new Date(matchedReceive.receivedDate) : null;
		const existingDate = rawDate && rawDate.getFullYear() > 1 ? rawDate : null;

		if (isVirtual) {
			// Virtual transfers finalise in full with the delivered date set to today; the field is read-only.
			setReceivedDate(existingDate ?? new Date());
			setIsDeliveredDateDisabled(true);
		} else if (existingDate) {
			setReceivedDate(existingDate);
			setIsDeliveredDateDisabled(false);
		} else {
			// Auto-populate from ASN dateDelivered for transfers (no invoice matching required)
			const asnWithDeliveredDate = transfer?.advancedShippingNotices?.find((asn) => asn.shipment?.dateDelivered);
			if (asnWithDeliveredDate?.shipment?.dateDelivered) {
				const inheritedDate = new Date(asnWithDeliveredDate.shipment.dateDelivered);
				if (!isNaN(inheritedDate.getTime()) && inheritedDate.getFullYear() > 1) {
					setReceivedDate(inheritedDate);
					setIsDeliveredDateDisabled(true);
				} else {
					setReceivedDate(null);
					setIsDeliveredDateDisabled(false);
				}
			} else {
				setReceivedDate(null);
				setIsDeliveredDateDisabled(false);
			}
		}

		if (transfer?.receives?.length) {
			receiveRef.current = matchedReceive;
			setReceive(matchedReceive);
			setSelectedCommercialAccount(matchedReceive?.inventoryStatus ?? "");
			setNotes(matchedReceive?.notes ?? "");
			setReceiveComments([
				...(matchedReceive?.comments ?? []),
				...(transfer.transfer.comments ?? []).map(mapTransferCommentToReceiveComment),
			]);
		} else if (transfer?.transfer?.comments?.length) {
			setReceiveComments(transfer.transfer.comments.map(mapTransferCommentToReceiveComment));
		}
	}, [transfer, matchedReceive, isVirtual]);

	useEffect(() => {
		if (!transfer || !receive) return;
		const { overReceivedItems, underReceivedItems } = getTransferReceiveVariances(receive, transfer.transfer);
		setVariances({ overReceivedItems, underReceivedItems });
	}, [receive, transfer]);

	const receiveIsEditable = useMemo(() => {
		return isEditable(receive);
	}, [receive]);

	const scanEditIsVisible = useMemo(() => {
		if (!receive?.status) return true;

		return (
			receive.status !== receiveStatus.completed &&
			receive.status !== receiveStatus.cancelled &&
			receive.status !== receiveStatus.receivedDirectly
		);
	}, [receive]);

	const receiveIsReadyToFinalise = useMemo(() => {
		return canBeFinalisedStatusList.includes(receive?.status ?? "");
	}, [receive]);

	useEffect(() => {
		if (!transfer?.transfer.lineItems) return;

		const { ticketPrices, promoPrices } = transfer.productPrices.reduce(
			(acc, item) => {
				acc.ticketPrices[item.sku] = item.ticketPrice;
				acc.promoPrices[item.sku] = item.promotionPrice;
				return acc;
			},
			{ ticketPrices: {} as Record<string, number>, promoPrices: {} as Record<string, number> }
		);

		// For transfers received directly in Shopkeeper there is no receive record, so the
		// received quantity lives on the transfer line item rather than a receive line item.
		const receivedDirectlyInHost = !receive && isTransferCompleted(transfer.transfer.status);

		const newLineItems = transfer.transfer.lineItems.map((li) => {
			let product = transfer.products.find((p) => p.id === li.sku);
			const productGroup: ProductGroupResponse | undefined = productGroups.find(
				(pg: ProductGroupResponse) => pg.id === product?.productGroupId
			);
			const inventory = transfer.customerOrders.inventory.find((i) => i.sku === li.sku);
			const receiveLineItem = receive?.receiveLineItems.find((rli) => rli.sku === li.sku);

			const invoicedQuantity = li.invoicedQuantity;
			const receivedQuantity = receiveLineItem?.receivedQuantity ?? (receivedDirectlyInHost ? li.receivedQuantity : 0);

			return {
				model: li.model,
				sku: li.sku,
				barcode: li.barcode,
				description: product?.description ?? "",
				artist: product?.artist ?? "",
				format: product?.format ?? "",
				amount: 0,
				unitCost: 0,
				gst: 0,
				invoiced: invoicedQuantity,
				originalReceived: receiveLineItem?.originalReceivedQuantity ?? 0,
				received: receivedQuantity,
				quantityAlreadyReceived: receiveLineItem?.quantityAlreadyReceived ?? 0,
				remainder: invoicedQuantity - receivedQuantity,
				productGroup: productGroup?.name ?? "",
				ticketPrice: ticketPrices[li.sku] ?? 0,
				promoPrice: promoPrices[li.sku] ?? 0,
				modifiedTimestamp: receiveLineItem?.modifiedDate
					? new Date(receiveLineItem.modifiedDate)
					: new Date(transfer.transfer.dateCreated),
				isExtraFromReceive: false,
				inboundLocation: receiveLineItem?.inboundLocation ?? "",
				hasLabel: product?.hasLabel,
				transferItem: { toLocation: { ...transfer.transfer.toLocation } },
				inventory: inventory ? { sku: inventory.sku, quantity: inventory.quantity } : undefined,
			} as InvoiceLineItem;
		});

		if (enabledPriorityStock || enabledArrivals) {
			sortItemsByPriorityStockFlag(newLineItems, transfer?.priorityStock ?? []);
		} else {
			sortItemsByModifiedDesc(newLineItems);
		}

		setLineItems(newLineItems);
	}, [productGroups, receive, receive?.id, transfer, enabledPriorityStock, enabledArrivals]);

	const locationBays = useMemo(() => {
		return mapLocationBaysFromReceiveLineItems(lineItems, currentInboundLocation);
	}, [lineItems, currentInboundLocation]);

	const [alertModal, setAlertModal] = useState<{
		type?: ModalAlertType;
		title?: string;
		isOpen: boolean;
		message: string | string[];
	}>({
		isOpen: false,
		message: "",
	});

	useEffect(() => {
		if (apiError) {
			let message = translateError(apiError);
			if (!message) message = "This transfer cannot be loaded right now - please try again later.";
			setAlertModal({ message, isOpen: true });
		} else {
			setAlertModal({ message: "", isOpen: false });
		}
	}, [apiError]);

	const closeModal = () => {
		setAlertModal({
			isOpen: false,
			message: "",
			type: undefined,
			title: undefined,
		});
	};

	const onEditLineItem = (lineItem: InvoiceLineItem) => {
		setCurrentProduct({ id: lineItem.sku, qty: lineItem.received });
	};

	const onDataChange = useCallback((data: { productId: string; qty: number }) => {
		setCurrentProduct({ id: data.productId, qty: data.qty });
	}, []);

	// Queue to manage sequential processing of scans, preventing race conditions
	// where rapid scans overwrite each other's state updates
	const scanQueue = useRef<Promise<void>>(Promise.resolve());

	const processScan = async (barcodeValue: string) => {
		window.scrollTo(0, 0);
		setReceiveHeaderExpanded(false);
		await onSaveItemClick(barcodeValue, null, "scan");
	};

	const onScan = (e: any) => {
		// Virtual transfers have no scan workflow - ignore any item barcode scanned on the PDA.
		if (isVirtual) return;

		if (assignLocationModal.isModalOpen) {
			setAssignLocationModal((prev) => ({ ...prev, scannedLocation: e.data.BarcodeValue }));
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
		scanQueue.current = scanQueue.current
			.then(() => processScan(e.data.BarcodeValue))
			.catch((error) => {
				// Ensure a failed scan does not stall subsequent scans
				// eslint-disable-next-line no-console
				console.error("Error processing scan", error);
				return Promise.resolve();
			});
	};

	// Keep a ref to the latest onScan handler so the event listener below never needs
	// to be removed/re-added on re-renders, preventing scans from being missed.
	const onScanRef = useRef(onScan);
	onScanRef.current = onScan;

	useEffect(() => {
		const handler = (e: Event) => onScanRef.current(e);
		document.addEventListener("onScan", handler);
		return () => document.removeEventListener("onScan", handler);
	}, []);

	const onSaveItemClick = async (inputValue: string, qty: number | null, action = "edit") => {
		if (enableAssignIbLocation && (isLocation(inputValue) || putAwayLocations.has(inputValue))) {
			// User is at a fulfilment centre, scanning an inbound location / put away location for the SKU
			if (isInboundLocation(inputValue, transfer!.transfer.toLocation.locationId) || putAwayLocations.has(inputValue)) {
				setCurrentInboundLocation(inputValue);
				setCurrentProduct({ id: "", qty: 0 });
			} else {
				setAlertModal({ ...modals.issueValidatingLocationBay, message: modals.issueValidatingLocationBay.paragraphs });
			}
			return;
		}

		let receiveLineItem: InvoiceLineItem | null = getScannedItemByProductId(lineItems, inputValue, transfer!.products);
		const validationResults = await validateScanAllowed(
			inputValue,
			receiveLineItem,
			receivedDate,
			transfer?.transfer,
			receive?.status
		);

		if (!validationResults.isValid) {
			setAlertModal({
				isOpen: true,
				message: validationResults.reason,
			});

			return;
		}

		if (!receiveLineItem) return;

		if (enableAssignIbLocation && !receiveLineItem.inboundLocation) {
			// Set the location bay on the line item
			receiveLineItem.inboundLocation = currentInboundLocation;
		}

		// At this point we have scanned a valid SKU, increment the quantity
		let newLineItems = await updateTransferLineItemQtyAsync(receiveLineItem, qty);

		setLineItems(newLineItems);
		setCurrentProduct({ id: receiveLineItem.sku, qty: receiveLineItem.received });

		saveEventHistoryToList({
			date: new Date(),
			staffId: graphProfile.staffId,
			staffName: graphProfile.displayName,
			transferSourceId: transfer?.transfer.sourceId,
			model: receiveLineItem.model,
			description: receiveLineItem.description,
			qty: receiveLineItem.received,
			action: action,
			sku: receiveLineItem.sku,
		});

		if (!receiveRef.current) {
			// First scan, auto-create the Receive
			await updateReceiveAsync(true);
		} else {
			// Existing Receive, increment the line item quantity
			try {
				if (
					enableAssignIbLocation &&
					receiveLineItem.inboundLocation &&
					receiveLineItem.inboundLocation !== currentInboundLocation
				) {
					// User will be required to confirm the location bay change, show a loading dialog so they don't start scanning other SKUs
					setIsShowLoadingDialog(true);
				}

				// First update the local state to keep UI responsive
				await updateReceiveAsync(false);

				// Then make the API call without awaiting it
				const { staffId, displayName } = graphProfile;
				// Don't await - this API is designed to update the receive silently
				void postLineItemReceived({
					...createReceiveLineItemFromInvoiceLineItem(newLineItems.find((rli) => rli.sku === receiveLineItem.sku)!),
					receiveId: receiveRef.current!.id,
					modifiedBy: displayName,
					modifiedById: staffId,
					modifiedDate: new Date().toISOString(),
				}).finally(() => {
					setIsShowLoadingDialog(false);
				});
			} catch (error) {
				setIsShowLoadingDialog(false);
				return;
			}
		}

		if (
			enableAssignIbLocation &&
			receiveLineItem.inboundLocation &&
			receiveLineItem.inboundLocation !== currentInboundLocation
		) {
			// User is at a fulfilment centre, scanning a SKU into a location bay that is different to the one
			// already assigned to this SKU. Show the "Assign Location" modal to confirm
			setAssignLocationModal({
				...assignLocationModal,
				sku: receiveLineItem.sku,
				currentLocation: receiveLineItem.inboundLocation,
				scannedLocation: "",
				context: "scanned",
				isModalOpen: true,
			});
			return;
		}
	};

	const updateTransferLineItemQtyAsync = async (receiveLineItem: InvoiceLineItem, qty: number | null) => {
		receiveLineItem.received = qty === null ? receiveLineItem.received + 1 : qty;
		receiveLineItem.remainder = receiveLineItem.invoiced - receiveLineItem.received;
		receiveLineItem.modifiedTimestamp = new Date();
		const newTransferLineItems = lineItems.map((item) => {
			return { ...item };
		});

		if (enabledPriorityStock || enabledArrivals) {
			sortItemsByPriorityStockFlag(newTransferLineItems, transfer?.priorityStock ?? []);
		} else {
			sortItemsByModifiedDesc(newTransferLineItems);
		}
		return newTransferLineItems;
	};

	const updateReceiveAsync = async (postToReceive: boolean = true) => {
		if (!receivedDate || receivedDate.getFullYear() <= 1) {
			setAlertModal({
				isOpen: true,
				message: "Delivered date must be set before receive can be updated.",
			});
			return;
		}

		let newReceive = createOrUpdateTransferReceive(
			transfer!,
			receiveRef.current,
			lineItems,
			graphProfile,
			receivedDate,
			selectedCommercialAccount,
			notes
		);
		receiveRef.current = newReceive;
		setReceive(newReceive);

		try {
			if (postToReceive) {
				await postReceiveAsync(newReceive);
			}

			return true;
		} catch (error) {
			setAlertModal({
				isOpen: true,
				message: "An error occurred while updating the receive.",
			});
			return false;
		}
	};

	const validateScanAllowed = async (
		scannedSku: string,
		receiveLineItem: InvoiceLineItem | null,
		receivedDate?: Date | null,
		transfer?: Transfer,
		receiveStatusValue?: string | null
	) => {
		let validationResult = { isValid: true, reason: "" };

		const isTransferStatusValid =
			transfer?.sourceSystem.toLowerCase() !== transferSourceSystem.Intranet &&
			transfer?.sourceSystem.toLowerCase() !== transferSourceSystem.Algo
				? transfer?.status === transferStatus.inTransit
				: true;
		if (
			transfer?.status === transferStatus.completed ||
			transfer?.status === transferStatus.completedWithVariances ||
			transfer?.status === transferStatus.completedManuallyRecievedInSK
		) {
			validationResult = {
				isValid: false,
				reason: "Transfer has been completed and cannot be received against.",
			};
		} else if (receiveStatusValue && blockEdiScanStatusList.includes(receiveStatusValue)) {
			validationResult = {
				isValid: false,
				reason: `Cannot scan items for a receive with status ${statusToLabel(receiveStatusValue)}`,
			};
		} else if (!isTransferStatusValid) {
			validationResult = {
				isValid: false,
				reason: "This transfer cannot be received in its current status.",
			};
		} else if (!receivedDate || receivedDate.getFullYear() <= 1) {
			validationResult = {
				isValid: false,
				reason: "Delivered date must be set",
			};
		} else if (enableAssignIbLocation && !currentInboundLocation) {
			validationResult = {
				isValid: false,
				reason: "Before receiving items, you must assign a location",
			};
		} else if (!receiveLineItem) {
			validationResult = {
				isValid: false,
				reason: `${scannedSku} does not belong to the transfer`,
			};
		}

		return validationResult;
	};

	const onLocationSubmit = async (previousLocation: string, updatedLocation: string, sku?: string) => {
		const newLineItems = editLocationBay(lineItems, previousLocation, updatedLocation, sku);
		setLineItems(newLineItems);

		setAssignLocationModal({
			...assignLocationModal,
			sku: "",
			currentLocation: "",
			context: "manual",
			scannedLocation: "",
			isModalOpen: false,
		});

		if (!receive) return;

		let successful: boolean;
		if (sku) {
			// First update the local state to keep UI responsive
			await updateReceiveAsync(false);

			// Then make the API call without awaiting it
			const { staffId, displayName } = graphProfile;
			// Using void operator to explicitly ignore the Promise (suppresses missing await warning)
			void postLineItemReceived({
				...createReceiveLineItemFromInvoiceLineItem(newLineItems.find((li) => li.sku === sku)!),
				receiveId: receive.id,
				modifiedBy: displayName,
				modifiedById: staffId,
				modifiedDate: new Date().toISOString(),
			});
			successful = true;
		} else {
			successful = (await updateReceiveAsync()) ?? false;
		}

		if (successful) {
			setAlertModal({ isOpen: true, title: "Successful Request", type: "success", message: "Location updated" });
		}
	};

	const onLocationNameChange = (currentLocation: string, sku?: string) => {
		setAssignLocationModal({
			...assignLocationModal,
			sku: sku ?? "",
			currentLocation,
			scannedLocation: "",
			context: "manual",
			isModalOpen: true,
		});
	};

	const onCloseModalAssignLocation = () => {
		setAssignLocationModal({
			...assignLocationModal,
			sku: "",
			currentLocation: "",
			context: "manual",
			scannedLocation: "",
			isModalOpen: false,
		});
	};

	const handleAccountChange = (value: string) => {
		setSelectedCommercialAccount(value);
	};

	const onSkuHistoryClick = (lineItem: InvoiceLineItem) => {
		setHistoryModalState({
			isModalOpen: true,
			sku: lineItem.sku,
		});
	};

	const onClearReceive = async () => {
		if (!receive) return;

		try {
			await postReceiveAsync({ ...receive, status: receiveStatus.cancelled });
			receiveRef.current = null;
			setReceive((prevReceive) => {
				return { ...prevReceive!, status: receiveStatus.cancelled };
			});
		} catch (error) {
			setAlertModal({
				isOpen: true,
				message: "An error occurred while cancelling the receive.",
			});
		}

		setClearReceiveModalIsOpen(false);
	};

	const onFinaliseSubmit = async (
		customerOrders: AllocatedCustomerOrder[],
		hasLabels: boolean,
		skipAutoOrderAllocation: boolean,
		skipProductLabelPrinting: boolean
	) => {
		if (!receivedDate || !transfer) return;

		setIsShowLoadingDialog(true);

		const { selectedPrinterId, selectedLabelPrinterId, userLocation } = pageContext;

		const { staffId, displayName } = graphProfile;

		let newReceive = createOrUpdateTransferReceive(
			transfer!,
			receive,
			lineItems,
			graphProfile,
			receivedDate,
			selectedCommercialAccount,
			notes
		);

		newReceive.receiver = displayName;
		newReceive.receiverId = staffId;
		newReceive.status = pendingReceive ? receiveStatus.pendingReceive : receiveStatus.completed;
		newReceive.failureReason = null;
		newReceive.receiptAttemptCount = 0;
		newReceive.transferRequestId = transfer.transfer.transferRequestId;

		try {
			await postSubmitReceiveAsync(
				newReceive,
				customerOrders,
				selectedPrinterId,
				selectedLabelPrinterId,
				userLocation,
				false,
				hasLabels,
				skipAutoOrderAllocation,
				skipProductLabelPrinting
			);
			setReceive(newReceive);
			await refreshTransfer();
			const isPendingReceive = receive?.status === receiveStatus.pendingReceive;
			setSnackbarAlert(
				isPendingReceive ? snackBarAlerts.success.submittedReceive : snackBarAlerts.success.finalisedReceive
			);
		} catch (e) {
			let message = modals.issueFinalisingReceive.paragraphs;
			if (e instanceof Error) {
				message.push(e.message);
			}

			setAlertModal({
				...modals.issueFinalisingReceive,
				message: message,
			});
		} finally {
			setReceiveFinaliseModalIsOpen(false);
			setIsShowLoadingDialog(false);
		}
	};

	const onClickVirtualFinalise = () => {
		if (!transfer) return;

		// Virtual transfers are received in full: every line item's received quantity is set to its sent quantity.
		const fullyReceivedLineItems = lineItems.map((li) => ({
			...li,
			received: li.invoiced,
			remainder: 0,
			modifiedTimestamp: new Date(),
		}));

		const newReceive = createOrUpdateTransferReceive(
			transfer,
			receiveRef.current,
			fullyReceivedLineItems,
			graphProfile,
			receivedDate ?? new Date(),
			selectedCommercialAccount,
			notes
		);
		receiveRef.current = newReceive;
		setReceive(newReceive);
		setReceiveFinaliseModalIsOpen(true);
	};

	const onClickFinalise = () => {
		if (isVirtual) {
			onClickVirtualFinalise();
			return;
		}

		if (!transfer || !receive) return;

		// Check for unverified transfers with quantity differences (HIGHEST PRIORITY)
		if (isUnverified) {
			const differences = getUnverifiedQuantityDifferences(receive, transfer.transfer, transfer.products);
			if (differences.length > 0) {
				setUnverifiedFinaliseModalIsOpen(true);
				return;
			}
			setReceiveFinaliseModalIsOpen(true);
			return;
		}

		// Check for variances
		// A variance occurs when the receiveLineItems.invoicedQuantity does not equal receiveLineItems.receivedQuantity
		const { overReceivedItems, underReceivedItems } = getTransferReceiveVariances(receive, transfer.transfer);

		if (overReceivedItems.length || underReceivedItems.length) {
			setVariances({ overReceivedItems, underReceivedItems });
		}

		// Show the appropriate modal
		setReceiveWithVarianceModalIsOpen(overReceivedItems.length > 0 || underReceivedItems.length > 0);
		setReceiveFinaliseModalIsOpen(!overReceivedItems.length && !underReceivedItems.length);
	};

	const setReceivedDirectStatus = useCallback(
		async (receive: Receive) => {
			try {
				await postReceiveAsync({ ...receive, status: receiveStatus.receivedDirectly });
				setReceive((prevReceive) => ({
					...prevReceive!,
					status: receiveStatus.receivedDirectly,
				}));
			} catch (error) {
				setAlertModal({
					isOpen: true,
					message: "An error occurred while updating the receive status.",
				});
			}
		},
		[postReceiveAsync]
	);

	const checkIfReceivedDirectly = useCallback(async () => {
		if (!receive || !transfer) return;

		if (isTransferReceivedDirectInHost(receive.status, transfer.transfer.status)) {
			setAlertModal({
				isOpen: true,
				message:
					"Whoa there! Looks as though this receive has been processed directly in Shopkeeper. As this receive was In Progress it is now cleared.",
			});

			await setReceivedDirectStatus(receive);
		}
	}, [setReceivedDirectStatus, receive, transfer]);

	useEffect(() => {
		checkIfReceivedDirectly();
	}, [checkIfReceivedDirectly]);

	return (
		<>
			<LoadingDialog isOpen={isPosting || isLoading || isShowLoadingDialog} message="Loading" />

			<ModalWithActionButton
				{...alertModal}
				title={alertModal.title ?? "Warning"}
				alertType={alertModal.type ?? "warning"}
				primaryButtonName="Ok"
				onPrimaryButtonClick={closeModal}
			/>

			<SnackbarAlert
				open={snackbarAlert.isOpen}
				message={snackbarAlert.message}
				severity={snackbarAlert.severity}
				onClose={() => {
					setSnackbarAlert({ ...snackbarAlert, isOpen: false });
				}}
			/>

			<Backdrop open={speedDialOpen || !receiveIsEditable} style={{ zIndex: 1000 }} />

			{transfer && (
				<>
					<Grid container direction="column">
						<ReceiveTransferHeaderDetails
							transfer={transfer.transfer}
							headerIsExpanded={receiveHeaderExpanded}
							onToggleHeader={setReceiveHeaderExpanded}
							receiveStatus={
								receive?.status ??
								(transfer.transfer.status === transferStatus.completed ||
								transfer.transfer.status === transferStatus.completedWithVariances ||
								transfer.transfer.status === transferStatus.completedManuallyRecievedInSK
									? receiveStatus.receivedDirectly
									: receiveStatus.open)
							}
							receiveFailureReason={receive?.failureReason}
							receivedDate={receivedDate}
							onReceiveDateChanged={setReceivedDate}
							isDeliveredDateDisabled={isDeliveredDateDisabled}
							comments={receiveComments}
							receive={receive}
							transferId={transfer.transfer.id}
							isUnverified={isUnverified}
							asns={transfer.advancedShippingNotices}
						/>

						{enableCommercialAccountSelection && (
							<CommercialSelect
								menuItems={commercialAccounts}
								onChange={handleAccountChange}
								currentValue={selectedCommercialAccount}
							/>
						)}

						{isVirtual
							? canFinaliseVirtual && (
									<Grid size="grow" sx={{ my: 2 }}>
										<Alert severity="info">
											This is a {getTransferTypeDisplayLabel(transfer?.transfer?.type)} transfer, click on Finalise to
											receive it in full.
										</Alert>
									</Grid>
								)
							: scanEditIsVisible && (
									<ScanEditWithSaveButton
										productId={currentProduct.id}
										productQty={currentProduct.qty}
										onDataChange={onDataChange}
										onSaveItemClick={onSaveItemClick}
										enableAssignIbLocation={enableAssignIbLocation}
									/>
								)}

						{(!enableAssignIbLocation || locationBays.length === 0) && (
							<MemorizedInvoiceLineItemTable
								onEditClick={onEditLineItem}
								onHistoryClick={onSkuHistoryClick}
								onRemoveClick={() => {
									/* noop - not supported by transfers */
								}}
								isDisableHistoryButton={!receive}
								invoiceLineItems={lineItems}
								isHardwareView={isHardwareView}
								isEditable={isVirtual ? false : receiveIsEditable}
								priorityStock={transfer.priorityStock}
								isArrivalsFeatureEnabled={enabledArrivals}
								isPriorityStockEnabled={enabledPriorityStock}
								isLocationBayEditable={false}
								onLocationNameChange={() => {}}
								receiveType={receiveType.transfer}
								hideOverReceivedMessage={isUnverified}
								hideReceivedRemainEditColumns={isVirtual}
							/>
						)}

						{enableAssignIbLocation && locationBays.length > 0 && (
							<>
								<CurrentLocationBayAlert currentLocation={currentInboundLocation} />

								{locationBays.map((locationBay, idx) => {
									return (
										<React.Fragment key={idx}>
											<LocationBay
												key={idx}
												locationName={locationBay}
												onLocationNameChange={onLocationNameChange}
												hasLineItems={lineItems.length > 0}
											>
												<MemorizedInvoiceLineItemTable
													onEditClick={onEditLineItem}
													onHistoryClick={onSkuHistoryClick}
													onRemoveClick={() => {
														/* noop - not supported by transfers */
													}}
													isDisableHistoryButton={!receive}
													invoiceLineItems={lineItems.filter((rli) => {
														return rli.inboundLocation === locationBay;
													})}
													isHardwareView={isHardwareView}
													isEditable={isVirtual ? false : receiveIsEditable}
													priorityStock={transfer.priorityStock}
													isArrivalsFeatureEnabled={enabledArrivals}
													isPriorityStockEnabled={enabledPriorityStock}
													isLocationBayEditable={receiveIsEditable}
													onLocationNameChange={onLocationNameChange}
													receiveType={receiveType.transfer}
													hideOverReceivedMessage={isUnverified}
													hideReceivedRemainEditColumns={isVirtual}
												/>
											</LocationBay>
										</React.Fragment>
									);
								})}
							</>
						)}
					</Grid>

					{assignLocationModal.isModalOpen && (
						<ModalAssignLocation
							isOpen={assignLocationModal.isModalOpen}
							onSubmit={onLocationSubmit}
							onCancel={onCloseModalAssignLocation}
							poLocation={transfer.transfer.toLocation.locationId.toString(10)}
							scannedInboundLocation={assignLocationModal.scannedLocation}
							currentLocation={assignLocationModal.currentLocation}
							sku={assignLocationModal.sku}
							context={assignLocationModal.context}
						/>
					)}

					{clearReceiveModalIsOpen && (
						<ModalWithActionButton
							title="Clear Receive"
							alertType={alertTypeEnum.WARNING as ModalAlertType}
							primaryButtonName="Yes"
							secondaryButtonName="No"
							onPrimaryButtonClick={() => onClearReceive()}
							onSecondaryButtonClick={() => setClearReceiveModalIsOpen(false)}
							isOpen={clearReceiveModalIsOpen}
							message={`Are you sure you want to clear this receive? ${
								receive?.status === receiveStatus.pendingFinalise
									? "This is a pending finalise receive, you must check that stock is received in Shopkeeper if it was intended to be!"
									: ""
							}`}
						/>
					)}

					{historyModalState.isModalOpen && (
						<ModalHistory
							title="Receive History"
							alertType={alertTypeEnum.INFO}
							primaryButtonName="Ok"
							onPrimaryButtonClick={() => setHistoryModalState({ ...historyModalState, isModalOpen: false })}
							onClose={() => setHistoryModalState({ ...historyModalState, isModalOpen: false })}
							isOpen={historyModalState.isModalOpen}
							children={
								<ReceiveHistory
									statusHistoryList={receive ? (receive.statusHistory ?? []) : []}
									transferSourceId={transfer ? transfer?.transfer.sourceId : ""}
									sku={historyModalState.sku}
								/>
							}
							size={modalSize.FULLSCREEN}
						/>
					)}

					{receive && receiveFinaliseModalIsOpen && (
						<ModalReceiveFinalise
							isOpen={receiveFinaliseModalIsOpen}
							hasExistingCredit={false}
							skipAllocation={false}
							onFinaliseSubmit={onFinaliseSubmit}
							onCancelFinalise={() => setReceiveFinaliseModalIsOpen(false)}
							locationId={transfer.transfer.toLocation.locationId.toString(10)}
							receive={receive}
							hasLabels={enableProductLabelAutoPrint && hasLabels}
							variances={variances}
							pendingReceive={pendingReceive}
							transferReason={transfer.transfer.reason}
						/>
					)}

					{receive && (
						<ModalTransferReceiveVariancesFinalise
							isOpen={receiveWithVarianceModalIsOpen}
							variances={variances}
							setReceiveFinaliseModalIsOpen={setReceiveFinaliseModalIsOpen}
							setPendingReceive={setPendingReceive}
							onCancelFinalise={() => setReceiveWithVarianceModalIsOpen(false)}
						/>
					)}

					{receive && transfer && unverifiedFinaliseModalIsOpen && (
						<ModalUnverifiedTransferFinalise
							isOpen={unverifiedFinaliseModalIsOpen}
							transfer={transfer.transfer}
							receive={receive}
							products={transfer.products}
							onCancelFinalise={() => setUnverifiedFinaliseModalIsOpen(false)}
							setReceiveFinaliseModalIsOpen={setReceiveFinaliseModalIsOpen}
							setPendingReceive={setPendingReceive}
						/>
					)}

					<CommentModal
						isOpen={commentModal.isOpen}
						isDisabled={!transfer?.transfer?.id}
						onClose={onCommentModalClose}
						comments={comments}
						onAddComment={onAddTransferComment(transfer?.transfer?.id ?? "")}
					/>

					<SpeedDialMenu
						isOpen={speedDialOpen}
						onClose={() => setSpeedDialOpen(false)}
						onOpen={() => setSpeedDialOpen(true)}
						actions={[
							{
								title: speedDialAction.save,
								onClick: async () => {
									let successful = await updateReceiveAsync();
									if (successful) {
										setSnackbarAlert(snackBarAlerts.success.savingReceive);
									}
									setSpeedDialOpen(false);
								},
								isDisplay: !isVirtual && receiveIsEditable,
							},
							{
								title: speedDialAction.finalise,
								onClick: () => {
									onClickFinalise();
									setSpeedDialOpen(false);
								},
								isDisplay: isVirtual ? canFinaliseVirtual : receiveIsReadyToFinalise && receiveIsEditable,
							},
							{
								title: speedDialAction.history,
								onClick: () => {
									setHistoryModalState({ sku: "", isModalOpen: true });
									setSpeedDialOpen(false);
								},
								isDisplay: receive !== null,
							},
							{
								title: isHardwareView ? speedDialAction.hardwareView : speedDialAction.softwareView,
								onClick: () => {
									setIsHardwareView(!isHardwareView);
									setSpeedDialOpen(false);
								},
								isDisplay: true,
							},
							{
								title: speedDialAction.clearReceive,
								onClick: () => {
									setClearReceiveModalIsOpen(true);
									setSpeedDialOpen(false);
								},
								isDisplay: canBeClearedStatusList.includes(receive?.status ?? ""),
							},
							{
								title: speedDialAction.scrollToTop,
								onClick: () => {
									window.scrollTo(0, 0);
									setSpeedDialOpen(false);
								},
								isDisplay: true,
							},
							{
								title: speedDialAction.comments,
								onClick: onCommentModalOpen,
								isDisplay: !!transfer?.transfer?.id,
							},
						]}
					/>
				</>
			)}
		</>
	);
};

export default ReceiveTransfer;
